import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { BorrowerSegment } from 'ng2-app/models/borrower/BorrowerSegment.struct';
import { BulkSubmissionBorrower } from 'ng2-app/models/borrower/BulkSubmissionBorrower.struct';
import { LoanProduct } from 'ng2-app/models/dto/loan-product.model';
import { BulkSubmissionService } from 'ng2-app/services/bulk-submission/bulk-submission.service';
import {
  BulkSubmitBorrowers, GetBorrowerSegments,
  GetBulkSubmissionBorrowers,
  GetBulkSubmittableLoanProducts,
} from 'ng2-app/store/npr-bulk-submission/bulk-submission.actions';
import {
  AddStackableNotificationError,
  AddStackableNotificationSuccess
} from 'ng2-app/store/stackable-notifications/StackableNotifications.actions';
import { catchError, tap } from 'rxjs/operators';

interface BulkSubmissionStateModel {
  borrowers: BulkSubmissionBorrower[];
  loading: boolean;
  borrowersLoading: boolean;
  loanProducts: LoanProduct[];
  submitting: boolean;
  segments: BorrowerSegment[];
}

@Injectable({
  providedIn: 'root',
})
@State<Partial<BulkSubmissionStateModel>>({
  name: 'BulkSubmission',
})
export class BulkSubmissionState {

  @Selector()
  static borrowers(state: BulkSubmissionStateModel) {
    return state.borrowers;
  }

  @Selector()
  static loanProducts(state: BulkSubmissionStateModel) {
    return state.loanProducts;
  }

  @Selector()
  static loading(state: BulkSubmissionStateModel) {
    return state.loading;
  }

  @Selector()
  static borrowersLoading(state: BulkSubmissionStateModel) {
    return state.borrowersLoading;
  }

  @Selector()
  static submitting(state: BulkSubmissionStateModel) {
    return state.submitting;
  }

  @Selector()
  static segments(state: BulkSubmissionStateModel) {
    return state.segments;
  }

  constructor(
    private bulkSubmissionService: BulkSubmissionService,
    private store: Store,
  ) {}

  @Action(GetBulkSubmissionBorrowers)
  GetBulkSubmissionBorrowers(
    ctx: StateContext<BulkSubmissionStateModel>,
    {}: GetBulkSubmissionBorrowers,
  ) {
    ctx.patchState({borrowersLoading: true});
    return this.bulkSubmissionService.getBorrowers()
      .pipe(
        catchError(err => {
          this.store.dispatch( new AddStackableNotificationError({
            title: 'Error',
            subTitle: 'Failed retrieving Borrowers data. Please contact the Product Support team.',
          }));
          ctx.patchState({borrowersLoading: false});
          throw err;
        }),
        tap( borrowers => {
          ctx.setState(patch({
            borrowers,
            borrowersLoading: false,
          }));
        }),
      );
  }

  @Action(BulkSubmitBorrowers)
  BulkSubmitBorrowers(
    ctx: StateContext<BulkSubmissionStateModel>,
    { borrowerIds, productIds }: BulkSubmitBorrowers,
  ) {
    ctx.patchState({submitting: true});
    return this.bulkSubmissionService.submitBorrowers(borrowerIds, productIds)
      .pipe(
        catchError(err => {
          this.store.dispatch( new AddStackableNotificationError({
            title: 'Error',
            subTitle: 'Failed to bulk-submit Borrowers. Please contact the Product Support team.',
          }));
          ctx.patchState({submitting: false});
          throw err;
        }),
        tap( () => {
          this.store.dispatch( new AddStackableNotificationSuccess({
            subTitle: 'Borrowers submitted successfully.',
          }));
          ctx.patchState({submitting: false});
        }),
      );
  }

  @Action(GetBulkSubmittableLoanProducts)
  GetBulkSubmittableLoanProducts(
    ctx: StateContext<BulkSubmissionStateModel>,
    { }: GetBulkSubmittableLoanProducts,
  ) {
    ctx.patchState({loading: true});
    return this.bulkSubmissionService.getSubmittableLoanProducts()
      .pipe(
        catchError(err => {
          this.store.dispatch( new AddStackableNotificationError({
            title: 'Error',
            subTitle: 'Failed retrieving bulk-submittable Loan Products. Please contact the Product Support team.',
          }));
          ctx.patchState({loading: false});
          throw err;
        }),
        tap( loanProducts => {
          ctx.patchState({
            loading: false,
            loanProducts,
          });
        }),
      );
  }

  @Action(GetBorrowerSegments)
  GetBorrowerSegments(
    ctx: StateContext<BulkSubmissionStateModel>,
    { }: GetBorrowerSegments,
  ) {
    ctx.patchState({loading: true});
    return this.bulkSubmissionService.getBorrowerSegments()
      .pipe(
        catchError(err => {
          this.store.dispatch( new AddStackableNotificationError({
            title: 'Error',
            subTitle: 'Failed retrieving Borrower Segments data. Please contact the Product Support team.',
          }));
          ctx.patchState({loading: false});
          throw err;
        }),
        tap( segments => {
          ctx.patchState({
            loading: false,
            segments,
          });
        }),
      );
  }

}
